import { useState } from "react";
import { Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { uploadFiles } from "@/services/file-service";

export interface UploadedFile {
  key?: string;
  name: string;
  size: number;
  type: string;
  progress?: number;
}

interface FileUploadZoneProps {
  onFilesUploaded: (files: UploadedFile[]) => void;
}

const FileUploadZone = ({ onFilesUploaded }: FileUploadZoneProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const [uploadingFiles, setUploadingFiles] = useState<UploadedFile[]>([]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const setProgress = (progress: number, name: string) => {
    setUploadingFiles((prev) =>
      prev.map((f) => (f.name === name ? { ...f, progress } : f))
    );
  };

  const handleFiles = async (files: File[]) => {
    try {
      setUploadingFiles(
        files.map((file) => ({
          name: file.name,
          size: file.size,
          type: file.type,
          progress: 0,
        }))
      );
      const responses = await uploadFiles(files, "shared-files", setProgress);

      onFilesUploaded(responses);
      toast.success(`${files.length} file(s) uploaded successfully`);
    } catch (error) {
      toast.error("Error uploading files");
      console.error("Upload error:", error);
    } finally {
      setUploadingFiles([]);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFiles(files);
    }
  };

  return (
    <div>
      <div
        className={`border-2 border-dashed rounded-lg p-6 sm:p-12 text-center ${
          isDragging ? "border-primary bg-primary/5" : "border-gray-300"
        } transition-colors`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Upload className="mx-auto h-12 w-12 text-gray-400" />
        <div className="mt-4">
          <p className="text-sm text-gray-600">
            Drag and drop your files here, or
          </p>
          <Input
            type="file"
            multiple
            className="hidden"
            onChange={handleFileInput}
            id="file-upload"
          />
          <label htmlFor="file-upload">
            <Button
              variant="outline"
              className="mt-2"
              onClick={() => document.getElementById("file-upload")?.click()}
            >
              Browse Files
            </Button>
          </label>
        </div>
      </div>
      {uploadingFiles.length > 0 && (
        <div className="mt-4 space-y-4">
          {uploadingFiles.map((file, index) => (
            <div key={index} className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">{file.name}</span>
                <span className="text-gray-500">{file.progress}%</span>
              </div>
              <Progress value={file.progress} className="h-2" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;
